import { ScrollObserverNamespace } from 'types/scroll-observer-namespace';

const DEFAULT_OPTIONS: ScrollObserverNamespace.Options = {
    root: null,
    threshold: [1.0],
    mode: 'intersection',
};

/**
 * @class ScrollObserver
 * @classdesc
 * A utility class that monitors scroll-related visibility changes for specific
 * target elements. It uses the IntersectionObserver API when available to
 * efficiently detect when an element enters or leaves the viewport. If the
 * browser does not support IntersectionObserver, the class falls back to a
 * native scroll listener and manually checks element visibility using
 * bounding client rectangles.
 */
export default class ScrollObserver {

    private callbacks: ScrollObserverNamespace.Callbacks;
    private readonly isIntersectionAvailable : boolean;
    private readonly options: ScrollObserverNamespace.Options;

    public constructor(
        private getObservedElements : () => HTMLElement[] | NodeListOf<HTMLElement>,
        callbacks : ScrollObserverNamespace.Callbacks,
        options : ScrollObserverNamespace.Options = {},
    ) {
        this.isIntersectionAvailable = 'IntersectionObserver' in window
            && 'IntersectionObserverEntry' in window
            && 'isIntersecting' in window.IntersectionObserverEntry.prototype;
        this.options = { ...DEFAULT_OPTIONS, ...options };
        this.callbacks = callbacks;
        this.initListeners();
    }

    /**
     * Initialize the listeners based on the availability
     * @protected
     */
    private initListeners() : void {
        if (this.options.mode === 'intersection' && this.isIntersectionAvailable) {
            this.useIntersectionObserver();
        }
        else {
            this.useEventListener();
            this.callbacks.onScrollEvent?.();
        }
    }

    /**
     * Initialize the IntersectionObserver API
     * @private
     */
    private useIntersectionObserver() : void {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => this.callbacks.onIntersectionEntry?.(entry));
        }, this.options);

        this.getObservedElements().forEach((element : HTMLElement) => observer.observe(element));
    }

    /**
     * Initialize event listeners for scroll, resize and orientationchange events
     * @private
     */
    private useEventListener() : void {
        const handler = () => this.callbacks.onScrollEvent?.();
        window.addEventListener('scroll', handler);
        window.addEventListener('resize', handler);
        window.addEventListener('orientationchange', handler);
    }

}
